import * as Sentry from '@sentry/react';
import { Observer } from 'mobx-react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactGA from 'react-ga4';
import { BrowserRouter, Route, Routes } from 'react-router-dom';

import { CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import Async from 'components/Async';
import ConfirmationProvider from 'components/Confirmation/ConfirmationProvider';
import 'i18n/config';
import { Settings } from 'luxon';
import App from 'modules/App';
import Auth from 'modules/Auth';
import ErrorPage from 'modules/Error';
import ImpersonateRoute from 'modules/Impersonate';
import InstallPwaBrowserView from 'modules/InstallPwaView';
import { SnackbarProvider } from 'notistack';
import Providers from 'providers';
import store from 'store';
import StyledNotificationSuccess from 'theme/snackBar/styled';
import isCampusApp from 'utils/isCampusApp';
import { registerSW } from 'virtual:pwa-register';

import CompletionMessageModal from '@common/components/CompletionMessageModal';
import StoreProvider from '@common/components/StoreProvider';
import { ROUTES } from '@common/constants/CampusRoutes';

import {
	//  env,
	initEnv,
} from './env';

ReactGA.initialize([
	{
		trackingId: 'G-SQ67Y369CG',
	},
]);

initEnv();

registerSW({ immediate: true });

if (navigator.storage?.persist) {
	navigator.storage.persist().then((granted) => {
		console.info('[PWA storage] persistent', granted);
	});
}

const root = createRoot(document.getElementById('root'));

Settings.defaultLocale = 'en';

const isStandalone =
	typeof window !== 'undefined' &&
	(window.matchMedia?.('(display-mode: standalone)').matches ||
		(window.navigator as Navigator & { standalone?: boolean }).standalone === true);

const isLocalhost =
	typeof window !== 'undefined' &&
	['localhost', '127.0.0.1', '::1', '0.0.0.0'].includes(window.location.hostname);

const PwaApp = (
	<Sentry.ErrorBoundary fallback={<p>An error has occurred</p>}>
		<StoreProvider store={store}>
			<Providers>
				<Observer>
					{() => (
						<BrowserRouter>
							<ThemeProvider theme={store.theme.theme}>
								<ConfirmationProvider>
									<CssBaseline enableColorScheme />
									<SnackbarProvider
										anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
										Components={{ success: StyledNotificationSuccess }}
									/>
									<CompletionMessageModal />
									<Sentry.ErrorBoundary
										fallback={({ resetError, error }) => (
											<ErrorPage error={error} resetError={resetError} />
										)}
									>
										<Async>
											{store.auth.isAuthorized ? (
												<Routes>
													<Route path={`${ROUTES.AUTH}/*`} element={<ImpersonateRoute />} />
													<Route path="*" element={<App />} />
												</Routes>
											) : (
												<Routes>
													<Route path={`${ROUTES.AUTH}/*`} element={<ImpersonateRoute />} />
													<Route path="*" element={<Auth />} />
												</Routes>
											)}
										</Async>
									</Sentry.ErrorBoundary>
								</ConfirmationProvider>
							</ThemeProvider>
						</BrowserRouter>
					)}
				</Observer>
			</Providers>
		</StoreProvider>
	</Sentry.ErrorBoundary>
);

root.render(isStandalone || isLocalhost || isCampusApp() ? PwaApp : InstallPwaBrowserView);
